import {
  SITE_COLOR,
  SITE_EASE,
  SITE_HEADER_BORDER_PX,
  SITE_HEADER_HEIGHT_PX,
  SITE_MEDIA,
  SITE_TOUCH_TARGET,
} from "src/domains/shared/constants/styles/site";
import { paddingInlineWithSpinner } from "../../siteKit/SiteButtonSpinner";
import { SITE_SESSION_ATTR } from "../siteRoutes";
import {
  HEADER_BREAKPOINTS_PX,
  HEADER_INNER_PADDING_PX,
  type HeaderSession,
} from "./headerBreakpoints";

/** 머리말 스타일 — 시안 `site.css` 의 `.gnb*`·`.dd*`·`.mnav`·`.wn-dot` 값 그대로. */
const NAV_TEXT = SITE_COLOR.navText;
const POP_ANIMATION = `sitePop .18s ${SITE_EASE.out}`;

type SxRules = Record<string, unknown>;
/** 로그인 확인 중 두 쪽을 모두 그릴 때 계정 버튼마다 다는, 보일 상태(`signed-in`·`signed-out`) */
export const SITE_SESSION_ONLY_ATTR = "data-site-session-only";
/** 관제 버튼에 다는 표시 — 이 버튼이 있는 계정은 오른쪽 묶음이 길어 더 넓은 폭에서 배치가 바뀐다 */
export const SITE_FLIGHT_HUB_ATTR = "data-site-flight-hub";

/**
 * 확인 중에는 문서의 저장된 로그인 판정(`<html>` 의 `SITE_SESSION_ATTR`, 첫 칠 전 부트 스크립트가 켬)으로 한쪽만 보인다
 * (marketing-site-shell 「머리말 계정 버튼은 첫 화면부터 로그인 상태를 따른다」). 숨긴 쪽은 `display: none` 이라 보조 기술과
 * Tab 순서에서도 빠진다. `target` 은 버튼 자신(`&`) 또는 목록 안 버튼(`& `)이다 — 버튼 자기 `display` 와 같은 스타일 묶음에서
 * 그 뒤에 두어, 우선순위를 계산하지 않고 문서 순서로만 덮는 환경(jsdom)에서도 같은 결과가 나오게 한다.
 */
const sessionOnlyRules = (target: "&" | "& ") => ({
  [`html[${SITE_SESSION_ATTR}] ${target}[${SITE_SESSION_ONLY_ATTR}="signed-out"], html:not([${SITE_SESSION_ATTR}]) ${target}[${SITE_SESSION_ONLY_ATTR}="signed-in"]`]:
    { display: "none" },
});

/** 관제 버튼이 있는 계정은 `signedIn` 에도 맞지만, 경계가 더 넓고 우선순위가 높아 먼저 바뀐다 */
const SESSION_SELECTOR: Record<HeaderSession, string> = {
  signedOut: `html:not([${SITE_SESSION_ATTR}])`,
  signedIn: `html[${SITE_SESSION_ATTR}]`,
  flightHub: `html[${SITE_SESSION_ATTR}]:has([${SITE_FLIGHT_HUB_ATTR}])`,
};

type LayoutLevel = "between" | "narrow" | "compact";
const LAYOUT_LEVELS: readonly LayoutLevel[] = ["between", "narrow", "compact"];

/** 넓은 폭부터 정렬 — 같은 우선순위면 뒤 규칙이 이기므로, 더 좁은 배치가 뒤에 와야 한다 */
const LAYOUT_STEPS = HEADER_BREAKPOINTS_PX.flatMap((breakpoints) =>
  LAYOUT_LEVELS.map((level) => ({
    level,
    media: `@media (max-width:${breakpoints[level] - 1}px)`,
    maxWidth: breakpoints[level] - 1,
    selector: `${SESSION_SELECTOR[breakpoints.session]}:lang(${
      breakpoints.lang
    }) &`,
  }))
).sort((a, b) => b.maxWidth - a.maxWidth);

/**
 * 폭이 줄어도 메뉴 양옆 틈(`NAV_SIDE_MIN_GAP_PX`)이 남도록 언어·로그인 상태별 경계(`HEADER_BREAKPOINTS_PX`)에서 배치를 바꾼다
 * (marketing-site-shell 「머리말 구성」). between — 메뉴를 남는 폭의 가운데로, narrow — 좁힌 여백·간격, compact — 메뉴 버튼 배치.
 * 태블릿 폭 이하는 언어와 상관없이 메뉴 버튼 배치다.
 */
const responsive = (styles: Partial<Record<LayoutLevel, SxRules>>) => {
  const rules: Record<string, SxRules> = {};
  LAYOUT_STEPS.forEach(({ level, media, selector }) => {
    const style = styles[level];
    if (style) rules[media] = { ...rules[media], [selector]: style };
  });
  return { ...rules, [SITE_MEDIA.tablet]: styles.compact ?? {} };
};

/** 머리말 글자(toss.im 머리말 실측과 같은 값): 링크·공유·언어·계정 버튼 모두 16px·500·자간 -0.02em */
const HEADER_TEXT = {
  fontSize: "16px",
  fontWeight: 500,
  letterSpacing: "-.02em",
} as const;

export const SITE_HEADER_STYLES = {
  skip: {
    position: "absolute",
    left: "12px",
    top: "-60px",
    zIndex: 100,
    background: SITE_COLOR.blue,
    color: "#fff",
    padding: "10px 14px",
    borderRadius: "6px",
    fontWeight: 600,
    "&:focus": { top: "12px" },
  },
  header: {
    position: "sticky",
    top: "env(safe-area-inset-top,0px)",
    zIndex: 60,
    // 맨 위에서는 불투명한 흰색 — 반투명 띠는 첫 칠에 아직 안 그려진 뒤(iOS 다크 모드의 검은 바탕)를 비쳐 회색으로 보인다.
    // 내용이 머리말 밑으로 지나가는 스크롤 뒤에만 반투명·흐림(유리)으로 바뀐다 — 흰 바탕 위라 바뀌는 순간은 보이지 않는다
    background: SITE_COLOR.bg,
    borderBottom: `${SITE_HEADER_BORDER_PX}px solid ${SITE_COLOR.line}`,
    transition: "transform .3s ease,box-shadow .25s ease",
    "&[data-scrolled]": {
      boxShadow: "0 2px 12px rgba(0,0,0,.06)",
      background: "rgba(255,255,255,.96)",
      "@supports (backdrop-filter:blur(8px))": {
        background: "rgba(255,255,255,.86)",
        backdropFilter: "saturate(180%) blur(12px)",
      },
    },
    // 아래로 스크롤하면 위로 미끄러져 사라진다 — 키보드 초점이 안에 있으면 보이는 채로 둔다
    "&[data-hidden]:not(:focus-within)": { transform: "translateY(-100%)" },
    [SITE_MEDIA.reducedMotion]: { transition: "none" },
  },
  // 로고 · 메뉴 · 오른쪽 묶음 — 넓으면 메뉴가 화면 정중앙(양옆 1fr 이 같은 폭), 좁으면 로고와 오른쪽 묶음 사이 남는 폭의 가운데
  inner: {
    height: `${SITE_HEADER_HEIGHT_PX}px`,
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    paddingInline: `${HEADER_INNER_PADDING_PX.wide}px`,
    position: "relative",
    whiteSpace: "nowrap",
    ...responsive({
      between: { gridTemplateColumns: "auto 1fr auto" },
      narrow: { paddingInline: `${HEADER_INNER_PADDING_PX.narrow}px` },
      compact: { display: "flex", paddingInline: "8px 4px" },
    }),
  },
  burger: {
    display: "none",
    ...responsive({
      compact: {
        display: "grid",
        marginLeft: "auto",
        placeItems: "center",
        width: "48px",
        height: "48px",
        color: SITE_COLOR.ink,
      },
    }),
  },
  // 「공유」 — 넓은 화면 오른쪽 묶음의 글자 버튼(메뉴 항목과 같은 생김새). 복사 직후(`data-copied`)는 파란 글자로 잠깐
  share: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: "36px",
    padding: "0 10px",
    border: 0,
    borderRadius: "6px",
    background: "none",
    font: "inherit",
    ...HEADER_TEXT,
    color: NAV_TEXT,
    cursor: "pointer",
    transition: "background-color .15s,color .15s",
    "&:hover": { background: SITE_COLOR.hoverTint },
    "&[data-copied]": { color: SITE_COLOR.blue },
    "& svg": { width: "18px", height: "18px" },
    ...responsive({ narrow: { padding: "0 8px", fontSize: "15px" } }),
  },
  logo: {
    ...SITE_TOUCH_TARGET,
    // 격자 칸(1fr)에 늘어나면 칸의 빈 자리를 눌러도 홈으로 간다
    justifySelf: "start",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "22px",
    fontWeight: 700,
    color: SITE_COLOR.heading,
    letterSpacing: 0,
    userSelect: "none",
    "&:hover": { opacity: 0.8 },
    "& svg": { width: "24px", height: "24px" },
    ...responsive({
      compact: {
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        fontSize: "20px",
        gap: "6px",
        "&:hover": { opacity: 1 },
        "& svg": { width: "20px", height: "20px" },
      },
    }),
  },
  nav: {
    display: "flex",
    gap: "4px",
    justifySelf: "center",
    ...responsive({ compact: { display: "none" } }),
  },
  navLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    height: "36px",
    padding: "0 12px",
    borderRadius: "6px",
    ...HEADER_TEXT,
    color: NAV_TEXT,
    transition: "background-color .15s,color .15s",
    "&:hover": { background: SITE_COLOR.hoverTint },
    // 지금 페이지(링크)·지금 페이지를 품은 패널 버튼: 색만 바꾼다 — 굵게 하면 글자 폭이 늘어 한 줄 경계 폭에서 넘친다
    '&[aria-current="page"], &[data-current]': { color: SITE_COLOR.blue },
    ...responsive({ narrow: { padding: "0 8px", fontSize: "15px" } }),
  },
  dot: {
    position: "relative",
    display: "inline-block",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: SITE_COLOR.blue,
    marginLeft: "4px",
    alignSelf: "flex-start",
    marginTop: "9px",
  },
  mobileDot: {
    position: "relative",
    display: "inline-block",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: SITE_COLOR.blue,
    marginLeft: "4px",
    verticalAlign: "top",
    marginTop: "4px",
  },
  serviceHolder: { position: "static", display: "flex", alignItems: "center" },
  servicePanel: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    margin: "0 auto",
    width: "min(1224px,calc(100% - 48px))",
    padding: "24px",
    background: "#fff",
    borderRadius: "0 0 20px 20px",
    boxShadow: `0 18px 50px rgba(0,0,0,.12),0 0 0 1px ${SITE_COLOR.line2}`,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    transformOrigin: "50% 0",
    animation: POP_ANIMATION,
    "&[hidden]": { display: "none" },
  },
  serviceCard: {
    display: "grid",
    gridTemplateColumns: "96px 1fr",
    gridTemplateRows: "auto auto auto",
    columnGap: "18px",
    alignItems: "start",
    padding: "18px",
    borderRadius: "14px",
    background: "#F7F8FA",
    color: SITE_COLOR.ink,
    transition: "background-color .15s",
    "&:hover": { background: "#EEF2F7" },
    "& img": {
      gridRow: "1/4",
      width: "96px",
      height: "96px",
      borderRadius: "14px",
      background: "#EEF2F7",
      objectFit: "cover",
    },
    "& b": {
      fontSize: "17px",
      fontWeight: 700,
      color: SITE_COLOR.ink,
      marginTop: "4px",
    },
    "& small": {
      display: "block",
      fontSize: "14px",
      fontWeight: 500,
      color: SITE_COLOR.ink2,
      lineHeight: 1.5,
      marginTop: "4px",
    },
    "& i": {
      fontStyle: "normal",
      fontSize: "13px",
      fontWeight: 600,
      color: SITE_COLOR.blue,
      marginTop: "10px",
    },
  },
  right: {
    justifySelf: "end",
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    ...responsive({ compact: { display: "none" } }),
  },
  mobileLocale: {
    display: "none",
    ...responsive({ compact: { display: "inline-flex" } }),
  },
  mobileMenu: {
    position: "absolute",
    top: `${SITE_HEADER_HEIGHT_PX}px`,
    right: "8px",
    minWidth: "240px",
    background: "#fff",
    borderRadius: "4px",
    boxShadow: "0 8px 10px 1px rgba(0,0,0,.14),0 3px 14px 2px rgba(0,0,0,.12)",
    padding: "8px 0",
    transformOrigin: "top right",
    animation: POP_ANIMATION,
    "&[hidden]": { display: "none" },
    "& a, & button": {
      display: "block",
      width: "100%",
      textAlign: "left",
      padding: "12px 16px",
      fontSize: "15px",
      fontWeight: 600,
      color: SITE_COLOR.ink2,
    },
    '& a[aria-current="page"], & [data-primary]': { color: SITE_COLOR.blue },
    // 진행 중인 항목(로그아웃) — 글자 앞 진행 표시 고리와 글자 사이를 띄운다
    '& button[aria-busy="true"]': {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "progress",
    },
    ...sessionOnlyRules("& "),
    // 「공유하기」 — 복사 직후 체크 아이콘 + 「링크 복사」(파란 글자)
    "& [data-share]": { display: "flex", alignItems: "center", gap: "6px" },
    "& [data-share] svg": { width: "18px", height: "18px" },
    "& [data-share][data-copied]": { color: SITE_COLOR.blue },
    // 가로 터치 화면: 머리말 아래가 낮아 한 열이면 아래 항목이 화면 밖으로 잘린다 — 두 열(왼쪽→오른쪽, 위→아래)로 놓고,
    // 그래도 넘치면 목록 안에서 스크롤한다
    [SITE_MEDIA.landscapeTouch]: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      width: "min(560px,calc(100vw - 16px))",
      maxHeight: `calc(100svh - ${SITE_HEADER_HEIGHT_PX}px - 16px)`,
      overflowY: "auto",
      overscrollBehavior: "contain",
    },
  },
} as const;

const PILL_PADDING_X = "20px";
/** 알약 안 고리·글자 사이 */
const PILL_GAP = "6px";

/** 머리말 오른쪽 버튼(시안 `.btn.btn-primary.gnb-login`) — 로그인·시작하기는 채운 알약, 로그아웃·관제 진입은 테두리 알약 */
const PILL_BASE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: PILL_GAP,
  height: "38px",
  padding: `0 ${PILL_PADDING_X}`,
  borderRadius: "12px",
  ...HEADER_TEXT,
  whiteSpace: "nowrap",
  transition:
    "background-color .15s ease,border-color .15s ease,color .15s ease,transform .15s ease,box-shadow .15s ease",
  "&:active": { transform: "scale(.98)" },
  "&:disabled": { opacity: 0.6, cursor: "default" },
  ...sessionOnlyRules("&"),
} as const;

export const SITE_ACCOUNT_BUTTON_STYLES = {
  primary: {
    ...PILL_BASE,
    background: SITE_COLOR.blue,
    color: "#fff",
    boxShadow: "0 6px 18px rgba(29,116,255,.28),0 2px 6px rgba(29,116,255,.18)",
    "&:hover": {
      background: SITE_COLOR.blueDark,
      boxShadow:
        "0 8px 22px rgba(29,116,255,.36),0 2px 6px rgba(29,116,255,.2)",
    },
  },
  outline: {
    ...PILL_BASE,
    border: "1px solid rgba(29,116,255,.5)",
    color: SITE_COLOR.blue,
    "&:hover": { background: SITE_COLOR.blueAlpha },
  },
  /** 진행 표시 고리를 단 버튼(로그아웃 중) — 진행 중을 비활성처럼 흐리지 않고, 알약 폭은 평소와 같다 */
  inProgress: {
    paddingInline: paddingInlineWithSpinner(PILL_PADDING_X, PILL_GAP),
    "&:disabled": { opacity: 1, cursor: "progress" },
  },
} as const;
